import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicLayout from '../../layouts/PublicLayout';
import publicBookingApi from '../../services/publicBookingApi';
import { formatDate } from '../../utils/dateFormatter';
import '../../styles/BookingLookup.css';
import { vatPercentSuffix } from '../../utils/vatPricing';

/**
 * BookingLookup - Look up booking by confirmation code
 * Routes:
 * - /booking/lookup (search form)
 * - /booking/:confirmationCode (direct booking view)
 */
const BookingLookup = () => {
  const { confirmationCode: urlConfirmationCode } = useParams();
  const navigate = useNavigate();

  const [confirmationCode, setConfirmationCode] = useState(urlConfirmationCode || '');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);

  // Review eligibility state
  const [reviewEligibility, setReviewEligibility] = useState({ canReview: false, reason: '' });
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Cancellation state
  const [cancelEmail, setCancelEmail] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelResult, setCancelResult] = useState(null);

  // Modification state
  const [modifyEmail, setModifyEmail] = useState('');
  const [modifyCheckIn, setModifyCheckIn] = useState('');
  const [modifyCheckOut, setModifyCheckOut] = useState('');
  const [modifyGuests, setModifyGuests] = useState(1);
  const [modifyLoading, setModifyLoading] = useState(false);
  const [modifyError, setModifyError] = useState(null);
  const [modifyResult, setModifyResult] = useState(null);
  const [modifyPricePreview, setModifyPricePreview] = useState(null);
  const [showModifyConfirmation, setShowModifyConfirmation] = useState(false);

  // Review state
  const [reviewEmail, setReviewEmail] = useState('');
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [reviewResult, setReviewResult] = useState(null);
  const [hoveredStar, setHoveredStar] = useState(0);

  // Refs for cleanup
  const cancelSuccessTimeoutRef = useRef(null);
  const modifySuccessTimeoutRef = useRef(null);
  const reviewSuccessTimeoutRef = useRef(null);

  // Calculate number of nights
  const calculateNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Load booking if confirmation code in URL
  useEffect(() => {
    if (urlConfirmationCode) {
      loadBooking(urlConfirmationCode);
    }
  }, [urlConfirmationCode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (cancelSuccessTimeoutRef.current) {
        clearTimeout(cancelSuccessTimeoutRef.current);
      }
      if (modifySuccessTimeoutRef.current) {
        clearTimeout(modifySuccessTimeoutRef.current);
      }
      if (reviewSuccessTimeoutRef.current) {
        clearTimeout(reviewSuccessTimeoutRef.current);
      }
    };
  }, []);

  const loadBooking = async (code) => {
    try {
      setLoading(true);
      setError(null);
      const response = await publicBookingApi.getBooking(code, 'ar');

      if (response.success) {
        setBooking(response.booking);
        // Check review eligibility after loading booking
        checkReviewEligibility(code);
      } else {
        setError(response.error || 'لم نتمكن من العثور على الحجز');
      }
    } catch (err) {
      console.error('Error loading booking:', err);
      setError('حدث خطأ في تحميل بيانات الحجز');
    } finally {
      setLoading(false);
    }
  };

  const checkReviewEligibility = async (code) => {
    try {
      const response = await publicBookingApi.checkReviewEligibility(code, 'ar');

      if (response.success) {
        setReviewEligibility({
          canReview: response.canReview,
          reason: response.reason || ''
        });
      }
    } catch (err) {
      console.error('❌ Error checking review eligibility:', err);
      // Fail silently - review button just won't show
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (confirmationCode.trim()) {
      navigate(`/booking/${confirmationCode.trim()}`);
    }
  };

  const handleCancelBooking = async (e, retryCount = 0) => {
    e.preventDefault();

    // Validation
    if (!cancelEmail.trim()) {
      setCancelError('يرجى إدخال البريد الإلكتروني');
      return;
    }

    // Safety check for booking.guestEmail
    if (!booking?.guestEmail) {
      setCancelError('خطأ في تحميل بيانات الحجز. يرجى المحاولة مرة أخرى.');
      return;
    }

    if (cancelEmail.toLowerCase().trim() !== booking.guestEmail.toLowerCase()) {
      setCancelError('البريد الإلكتروني غير متطابق');
      return;
    }

    const MAX_RETRIES = 2;

    try {
      setCancelLoading(true);
      setCancelError(null);

      const response = await publicBookingApi.cancelBooking(
        booking.confirmationCode,
        {
          guestEmail: cancelEmail.trim(),
          reason: cancelReason.trim() || 'لا يوجد سبب'
        },
        'ar'
      );

      if (response.success) {
        setCancelResult(response);
        // Reload booking to show updated status (with cleanup and error handling)
        cancelSuccessTimeoutRef.current = setTimeout(async () => {
          try {
            await loadBooking(booking.confirmationCode);
            setShowCancelModal(false);
            resetCancelModal();
          } catch (reloadErr) {
            console.error('Failed to reload booking after cancellation:', reloadErr);
            // Still close modal - cancellation succeeded even if reload failed
            // User can manually refresh if needed
            setShowCancelModal(false);
            resetCancelModal();
          }
        }, 3000);
      } else {
        setCancelError(response.error || 'حدث خطأ في إلغاء الحجز');
      }
    } catch (err) {
      console.error('Error cancelling booking:', err);

      // Retry on network errors (timeout, connection refused, etc.)
      const isNetworkError = !err.response || err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK';

      if (isNetworkError && retryCount < MAX_RETRIES) {
        // Wait before retry (exponential backoff: 1s, 2s)
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return handleCancelBooking(e, retryCount + 1);
      }

      // Show appropriate error message
      if (isNetworkError) {
        setCancelError('فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.');
      } else {
        setCancelError(err.response?.data?.error || 'حدث خطأ في إلغاء الحجز');
      }
    } finally {
      setCancelLoading(false);
    }
  };

  const resetCancelModal = () => {
    // Clear any pending timeout
    if (cancelSuccessTimeoutRef.current) {
      clearTimeout(cancelSuccessTimeoutRef.current);
      cancelSuccessTimeoutRef.current = null;
    }

    setCancelEmail('');
    setCancelReason('');
    setCancelError(null);
    setCancelResult(null);
    setCancelLoading(false);
  };

  const handleCheckNewPrice = async (e) => {
    e.preventDefault();

    // Validation
    if (!modifyEmail.trim()) {
      setModifyError('يرجى إدخال البريد الإلكتروني');
      return;
    }

    if (!booking?.guestEmail) {
      setModifyError('خطأ في تحميل بيانات الحجز. يرجى المحاولة مرة أخرى.');
      return;
    }

    if (modifyEmail.toLowerCase().trim() !== booking.guestEmail.toLowerCase()) {
      setModifyError('البريد الإلكتروني غير متطابق');
      return;
    }

    if (!modifyCheckIn || !modifyCheckOut) {
      setModifyError('يرجى تحديد تواريخ الوصول والمغادرة');
      return;
    }

    if (new Date(modifyCheckIn) >= new Date(modifyCheckOut)) {
      setModifyError('تاريخ المغادرة يجب أن يكون بعد تاريخ الوصول');
      return;
    }

    try {
      setModifyLoading(true);
      setModifyError(null);

      // Get property ID with multiple fallback strategies
      let propertyId;

      if (booking.property?._id) {
        // Property is an object with _id
        propertyId = booking.property._id;
      } else if (typeof booking.property === 'string') {
        // Property is already a string ID
        propertyId = booking.property;
      } else {
        // Last resort: reload booking to get proper data
        console.warn('Property ID not found, reloading booking data...');
        setModifyError('يرجى إعادة تحميل الصفحة والمحاولة مرة أخرى');
        setModifyLoading(false);
        return;
      }

      // Check availability and get new pricing
      // Pass bookingId so backend can exclude this booking from availability check
      const response = await publicBookingApi.checkAvailability(
        propertyId,
        {
          checkIn: modifyCheckIn,
          checkOut: modifyCheckOut,
          guests: parseInt(modifyGuests),
          bookingId: booking._id
        },
        'ar'
      );

      if (response.success && response.available) {
        const oldTotal = booking.totalAmount;
        const newTotal = response.rate.total;
        const priceDifference = newTotal - oldTotal;

        setModifyPricePreview({
          ...response.rate,
          oldTotal,
          newTotal,
          priceDifference
        });
        setShowModifyConfirmation(true);
      } else {
        setModifyError(response.error || 'التواريخ الجديدة غير متاحة');
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setModifyError(err.response?.data?.error || 'حدث خطأ في التحقق من التوفر');
    } finally {
      setModifyLoading(false);
    }
  };

  const handleConfirmModification = async () => {
    try {
      setModifyLoading(true);
      setModifyError(null);

      const response = await publicBookingApi.modifyBooking(
        booking.confirmationCode,
        {
          guestEmail: modifyEmail.trim(),
          checkIn: modifyCheckIn,
          checkOut: modifyCheckOut,
          numberOfGuests: parseInt(modifyGuests)
        },
        'ar'
      );

      if (response.success) {
        setModifyResult(response);
        // Reload booking to show updated details
        modifySuccessTimeoutRef.current = setTimeout(async () => {
          try {
            await loadBooking(booking.confirmationCode);
            setShowModifyModal(false);
            resetModifyModal();
          } catch (reloadErr) {
            console.error('Failed to reload booking after modification:', reloadErr);
            setShowModifyModal(false);
            resetModifyModal();
          }
        }, 3000);
      } else {
        setModifyError(response.error || 'حدث خطأ في تعديل الحجز');
      }
    } catch (err) {
      console.error('Error modifying booking:', err);
      setModifyError(err.response?.data?.error || 'حدث خطأ في تعديل الحجز');
    } finally {
      setModifyLoading(false);
    }
  };

  const resetModifyModal = () => {
    if (modifySuccessTimeoutRef.current) {
      clearTimeout(modifySuccessTimeoutRef.current);
      modifySuccessTimeoutRef.current = null;
    }

    setModifyEmail('');
    setModifyCheckIn('');
    setModifyCheckOut('');
    setModifyGuests(1);
    setModifyError(null);
    setModifyResult(null);
    setModifyPricePreview(null);
    setShowModifyConfirmation(false);
    setModifyLoading(false);
  };

  const openModifyModal = () => {
    // Pre-fill with current booking data
    setModifyCheckIn(booking.checkIn ? booking.checkIn.split('T')[0] : '');
    setModifyCheckOut(booking.checkOut ? booking.checkOut.split('T')[0] : '');
    setModifyGuests(booking.numberOfGuests || 1);
    setShowModifyModal(true);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    // Validation
    if (!reviewEmail.trim()) {
      setReviewError('يرجى إدخال البريد الإلكتروني');
      return;
    }

    if (!booking?.guestEmail) {
      setReviewError('خطأ في تحميل بيانات الحجز');
      return;
    }

    if (reviewEmail.toLowerCase().trim() !== booking.guestEmail.toLowerCase()) {
      setReviewError('البريد الإلكتروني غير متطابق');
      return;
    }

    if (reviewRating === 0) {
      setReviewError('يرجى تحديد التقييم');
      return;
    }

    if (!reviewComment.trim()) {
      setReviewError('يرجى كتابة تعليق');
      return;
    }

    if (reviewComment.length > 2000) {
      setReviewError('التعليق طويل جداً (الحد الأقصى 2000 حرف)');
      return;
    }

    try {
      setReviewLoading(true);
      setReviewError(null);

      const response = await publicBookingApi.submitReview(
        booking.confirmationCode,
        {
          rating: reviewRating,
          comment: reviewComment.trim(),
          guestEmail: reviewEmail.trim(),
          language: 'ar'
        },
        'ar'
      );

      if (response.success) {
        setReviewResult(response);
        // Close modal after 3 seconds
        reviewSuccessTimeoutRef.current = setTimeout(() => {
          setShowReviewModal(false);
          resetReviewModal();
          // Update eligibility (guest can't review again)
          setReviewEligibility({ canReview: false, reason: 'تم تقييم هذا الحجز مسبقاً' });
        }, 3000);
      } else {
        setReviewError(response.error || 'فشل في إرسال التقييم');
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setReviewError(err.response?.data?.error || 'حدث خطأ في إرسال التقييم');
    } finally {
      setReviewLoading(false);
    }
  };

  const resetReviewModal = () => {
    if (reviewSuccessTimeoutRef.current) {
      clearTimeout(reviewSuccessTimeoutRef.current);
      reviewSuccessTimeoutRef.current = null;
    }

    setReviewEmail('');
    setReviewRating(0);
    setReviewComment('');
    setReviewError(null);
    setReviewResult(null);
    setReviewLoading(false);
    setHoveredStar(0);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'في انتظار الدفع', class: 'pending', icon: 'clock-history' },
      confirmed: { label: 'مؤكد', class: 'confirmed', icon: 'check-circle' },
      paid: { label: 'تم الدفع', class: 'paid', icon: 'check-circle-fill' },
      cancelled: { label: 'ملغى', class: 'cancelled', icon: 'x-circle' },
      completed: { label: 'مكتمل', class: 'completed', icon: 'check-all' }
    };

    const statusInfo = statusMap[status] || statusMap.pending;

    return (
      <span className={`status-badge ${statusInfo.class}`}>
        <i className={`bi bi-${statusInfo.icon}`}></i>
        {statusInfo.label}
      </span>
    );
  };

  // Search form (no booking loaded yet)
  if (!booking && !loading) {
    return (
      <PublicLayout>
        <div className="booking-lookup-page">
          <div className="lookup-container">
            <div className="lookup-header">
              <i className="bi bi-search"></i>
              <h1>البحث عن حجز</h1>
              <p>أدخل رمز الحجز الخاص بك لعرض التفاصيل</p>
            </div>

            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-group">
                <input
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value.toUpperCase())}
                  placeholder="HM-67E62F14"
                  className="search-input"
                  dir="ltr"
                  required
                />
                <button type="submit" className="search-btn">
                  <i className="bi bi-search"></i>
                  بحث
                </button>
              </div>
              {error && <p className="search-error">{error}</p>}
            </form>

            <div className="help-section">
              <h3>كيف أجد رمز الحجز؟</h3>
              <ul>
                <li>
                  <i className="bi bi-envelope"></i>
                  تحقق من بريدك الإلكتروني - رسالة تأكيد الحجز
                </li>
                <li>
                  <i className="bi bi-phone"></i>
                  تحقق من رسائل SMS الخاصة بك
                </li>
                <li>
                  <i className="bi bi-chat-dots"></i>
                  اتصل بنا: <a href="tel:+966501234567">+966 50 123 4567</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <PublicLayout showLanguageSwitcher={false}>
        <div className="booking-lookup-loading">
          <div className="spinner"></div>
          <p>جاري تحميل تفاصيل الحجز...</p>
        </div>
      </PublicLayout>
    );
  }

  // Booking details view
  return (
    <PublicLayout showLanguageSwitcher={false}>
      <div className="booking-lookup-page">
        <div className="lookup-container">

          {/* Booking Header */}
          <div className="booking-header-card">
            <div className="booking-title-row">
              <div>
                <h1>{booking.property?.name}</h1>
                <p className="booking-code">رمز الحجز: {booking.confirmationCode}</p>
              </div>
              {getStatusBadge(booking.status)}
            </div>
          </div>

          {/* Booking Details */}
          <div className="booking-info-card">
            <h2 className="section-title">تفاصيل الحجز</h2>

            <div className="info-grid">
              <div className="info-item">
                <div className="info-icon">
                  <i className="bi bi-calendar-check"></i>
                </div>
                <div className="info-content">
                  <span className="info-label">تاريخ الوصول</span>
                  <span className="info-value">{formatDate(booking.checkIn)}</span>
                  <span className="info-hint">من الساعة {booking.property?.checkInTime || '15:00'}</span>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <i className="bi bi-calendar-x"></i>
                </div>
                <div className="info-content">
                  <span className="info-label">تاريخ المغادرة</span>
                  <span className="info-value">{formatDate(booking.checkOut)}</span>
                  <span className="info-hint">قبل الساعة {booking.property?.checkOutTime || '11:00'}</span>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <i className="bi bi-moon-stars"></i>
                </div>
                <div className="info-content">
                  <span className="info-label">عدد الليالي</span>
                  <span className="info-value">
                    {calculateNights(booking.checkIn, booking.checkOut)}
                  </span>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <i className="bi bi-people"></i>
                </div>
                <div className="info-content">
                  <span className="info-label">عدد الضيوف</span>
                  <span className="info-value">
                    {booking.numberOfGuests}
                  </span>
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="price-summary-box">
              <h3>ملخص السعر</h3>
              <div className="price-row">
                <span>قبل الضريبة</span>
                <span>{Number(booking.rate?.subtotal ?? booking.totalAmount ?? 0).toFixed(2)} ريال</span>
              </div>
              {/* Gated on presence, not truthiness — a zero-rated stay must show its own
                  0.00 line rather than silently losing the row. The percentage comes from
                  the booking's own figures, never from today's default rate. */}
              {booking.rate?.tax != null && (
                <div className="price-row">
                  <span>الضريبة{vatPercentSuffix(booking.rate?.subtotal, booking.rate?.tax)}</span>
                  <span>{Number(booking.rate.tax).toFixed(2)} ريال</span>
                </div>
              )}
              <hr />
              <div className="price-row total">
                <span>المبلغ الإجمالي</span>
                <span>{Number(booking.rate?.total ?? booking.totalAmount ?? 0).toFixed(2)} ريال</span>
              </div>
            </div>
          </div>

          {/* Guest Information */}
          <div className="guest-info-card">
            <h2 className="section-title">معلومات الضيف</h2>
            <div className="guest-details">
              <div className="guest-detail">
                <i className="bi bi-person"></i>
                <span>{booking.guestName}</span>
              </div>
              <div className="guest-detail">
                <i className="bi bi-envelope"></i>
                <span>{booking.guestEmail}</span>
              </div>
              <div className="guest-detail">
                <i className="bi bi-phone"></i>
                <span>{booking.guestPhone}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="booking-actions">
            <button
              onClick={() => window.print()}
              className="btn-secondary"
            >
              <i className="bi bi-printer"></i>
              طباعة
            </button>

            {/* Review Button - Only show if eligible */}
            {(() => {
              return reviewEligibility.canReview && (
                <button
                  onClick={() => setShowReviewModal(true)}
                  className="btn-primary"
                  style={{ backgroundColor: '#FFD700', borderColor: '#FFD700', color: '#000' }}
                >
                  <i className="bi bi-star-fill"></i>
                  اترك تقييماً
                </button>
              );
            })()}

            {booking.status !== 'cancelled' && booking.status !== 'completed' && (
              <>
                <button
                  onClick={openModifyModal}
                  className="btn-primary"
                  disabled={!booking.guestEmail}
                >
                  <i className="bi bi-pencil"></i>
                  تعديل الحجز
                </button>

                <button
                  onClick={() => setShowCancelModal(true)}
                  className="btn-danger"
                  disabled={!booking.guestEmail}
                >
                  <i className="bi bi-x-circle"></i>
                  إلغاء الحجز
                </button>
              </>
            )}

            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
            >
              <i className="bi bi-house"></i>
              الرئيسية
            </button>
          </div>

          {/* Support */}
          <div className="support-notice">
            <i className="bi bi-headset"></i>
            <p>
              هل تحتاج مساعدة؟ اتصل بنا على{' '}
              <a href="tel:+966501234567">+966 50 123 4567</a>
            </p>
          </div>
        </div>
      </div>

      {/* Modify Modal */}
      {showModifyModal && (
        <div
          className={`modal-overlay ${modifyLoading ? 'loading' : ''}`}
          onClick={() => {
            if (!modifyLoading) {
              setShowModifyModal(false);
              resetModifyModal();
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {!modifyResult ? (
              <>
                <h3>تعديل الحجز</h3>

                <div className="cancel-warning">
                  <i className="bi bi-info-circle"></i>
                  <p>قم بتعديل تواريخ الحجز أو عدد الضيوف</p>
                </div>

                {!showModifyConfirmation ? (
                  <form onSubmit={handleCheckNewPrice} className="cancel-form">
                  <div className="form-group">
                    <label>البريد الإلكتروني للتأكيد *</label>
                    <input
                      type="email"
                      value={modifyEmail}
                      onChange={(e) => setModifyEmail(e.target.value)}
                      placeholder="example@email.com"
                      required
                      disabled={modifyLoading}
                      dir="ltr"
                      className="form-control"
                    />
                    <small>يرجى إدخال البريد الإلكتروني المستخدم في الحجز للتحقق من هويتك</small>
                  </div>

                  <div className="form-group">
                    <label>تاريخ الوصول *</label>
                    <input
                      type="date"
                      value={modifyCheckIn}
                      onChange={(e) => setModifyCheckIn(e.target.value)}
                      required
                      disabled={modifyLoading}
                      className="form-control"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="form-group">
                    <label>تاريخ المغادرة *</label>
                    <input
                      type="date"
                      value={modifyCheckOut}
                      onChange={(e) => setModifyCheckOut(e.target.value)}
                      required
                      disabled={modifyLoading}
                      className="form-control"
                      min={modifyCheckIn || new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div className="form-group">
                    <label>عدد الضيوف *</label>
                    <input
                      type="number"
                      value={modifyGuests}
                      onChange={(e) => setModifyGuests(e.target.value)}
                      min="1"
                      required
                      disabled={modifyLoading}
                      className="form-control"
                    />
                  </div>

                  {modifyError && (
                    <div className="alert alert-danger" dir="rtl">
                      <i className="bi bi-exclamation-circle"></i> {modifyError}
                    </div>
                  )}

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={() => { setShowModifyModal(false); resetModifyModal(); }}
                      className="btn-secondary"
                      disabled={modifyLoading}
                    >
                      إلغاء
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={modifyLoading}
                    >
                      {modifyLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          جاري التحقق...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-calculator"></i>
                          احسب السعر الجديد
                        </>
                      )}
                    </button>
                  </div>
                </form>
                ) : (
                  /* Price Confirmation Step */
                  <div className="modify-confirmation" dir="rtl">
                    <div className="alert alert-info">
                      <h5><i className="bi bi-info-circle"></i> معلومات السعر الجديد</h5>

                      <div style={{ marginTop: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span>عدد الليالي:</span>
                          <strong>{modifyPricePreview?.nights}</strong>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span>الإجمالي الفرعي:</span>
                          <span>{modifyPricePreview?.subtotal} ريال</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span>الضريبة (15%):</span>
                          <span>{modifyPricePreview?.tax} ريال</span>
                        </div>
                        <hr />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                          <strong>المبلغ الإجمالي الجديد:</strong>
                          <strong>{modifyPricePreview?.newTotal} ريال</strong>
                        </div>

                        {modifyPricePreview?.priceDifference !== 0 && (
                          <div style={{
                            padding: '0.75rem',
                            borderRadius: '0.25rem',
                            backgroundColor: modifyPricePreview.priceDifference > 0 ? '#fff3cd' : '#d1ecf1',
                            border: `1px solid ${modifyPricePreview.priceDifference > 0 ? '#ffc107' : '#0dcaf0'}`,
                            marginTop: '1rem'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontWeight: 'bold' }}>
                                {modifyPricePreview.priceDifference > 0 ? 'فرق السعر (إضافي):' : 'فرق السعر (استرداد):'}
                              </span>
                              <span style={{
                                fontSize: '1.25rem',
                                fontWeight: 'bold',
                                color: modifyPricePreview.priceDifference > 0 ? '#856404' : '#055160'
                              }}>
                                {Math.abs(modifyPricePreview.priceDifference)} ريال
                                {modifyPricePreview.priceDifference > 0 ? ' ▲' : ' ▼'}
                              </span>
                            </div>
                            <small style={{ display: 'block', marginTop: '0.5rem', opacity: 0.8 }}>
                              {modifyPricePreview.priceDifference > 0
                                ? 'يرجى دفع الفرق بعد التأكيد'
                                : 'سيتم رد الفرق خلال 5-7 أيام عمل'
                              }
                            </small>
                          </div>
                        )}

                        {modifyPricePreview?.priceDifference === 0 && (
                          <div style={{
                            padding: '0.75rem',
                            borderRadius: '0.25rem',
                            backgroundColor: '#d1e7dd',
                            border: '1px solid #198754',
                            marginTop: '1rem'
                          }}>
                            <i className="bi bi-check-circle-fill" style={{ color: '#198754' }}></i>
                            <strong> نفس السعر - لا يوجد فرق</strong>
                          </div>
                        )}
                      </div>
                    </div>

                    {modifyError && (
                      <div className="alert alert-danger" style={{ marginTop: '1rem' }}>
                        <i className="bi bi-exclamation-circle"></i> {modifyError}
                      </div>
                    )}

                    <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                      <button
                        type="button"
                        onClick={() => setShowModifyConfirmation(false)}
                        className="btn-secondary"
                        disabled={modifyLoading}
                      >
                        <i className="bi bi-arrow-right"></i> رجوع
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmModification}
                        className="btn-primary"
                        disabled={modifyLoading}
                      >
                        {modifyLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            جاري التعديل...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle"></i>
                            تأكيد التعديل
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Success Message */
              <div className="cancel-success">
                <i className="bi bi-check-circle-fill text-success"></i>
                <h3>تم تعديل الحجز بنجاح</h3>
                <p>{modifyResult.message}</p>

                {modifyResult.pricing && (
                  <div className="refund-info">
                    <h4>تفاصيل السعر الجديد</h4>
                    <p>عدد الليالي: {modifyResult.pricing.nights}</p>
                    <p>الإجمالي الفرعي: {modifyResult.pricing.subtotal} ريال</p>
                    <p>الضريبة (15%): {modifyResult.pricing.tax} ريال</p>
                    <p><strong>المبلغ الإجمالي: {modifyResult.pricing.total} ريال</strong></p>
                    {modifyResult.booking?.priceDifference !== 0 && (
                      <small>
                        {modifyResult.booking.priceDifference > 0
                          ? `يرجى دفع الفرق: ${modifyResult.booking.priceDifference} ريال`
                          : `سيتم رد: ${Math.abs(modifyResult.booking.priceDifference)} ريال`
                        }
                      </small>
                    )}
                  </div>
                )}

                <button
                  onClick={() => { setShowModifyModal(false); resetModifyModal(); }}
                  className="btn-primary"
                >
                  إغلاق
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div
          className={`modal-overlay ${reviewLoading ? 'loading' : ''}`}
          onClick={() => {
            if (!reviewLoading) {
              setShowReviewModal(false);
              resetReviewModal();
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {!reviewResult ? (
              <>
                <h3>تقييم الإقامة</h3>

                <div className="cancel-warning" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                  <i className="bi bi-star-fill" style={{ color: '#FFD700' }}></i>
                  <p>شاركنا تجربتك في هذا العقار</p>
                </div>

                <form onSubmit={handleSubmitReview} className="cancel-form">
                  <div className="form-group">
                    <label>البريد الإلكتروني للتأكيد *</label>
                    <input
                      type="email"
                      value={reviewEmail}
                      onChange={(e) => setReviewEmail(e.target.value)}
                      placeholder="example@email.com"
                      required
                      disabled={reviewLoading}
                      dir="ltr"
                      className="form-control"
                    />
                    <small>يرجى إدخال البريد الإلكتروني المستخدم في الحجز</small>
                  </div>

                  <div className="form-group">
                    <label>التقييم *</label>
                    <div style={{ display: 'flex', gap: '8px', fontSize: '2rem', justifyContent: 'center', padding: '12px 0' }}>
                      {[1, 2, 3, 4, 5].map((star) => {
                        // Use hovered star if hovering, otherwise use selected rating
                        const isActive = (hoveredStar || reviewRating) >= star;

                        return (
                          <i
                            key={star}
                            className={isActive ? 'bi bi-star-fill' : 'bi bi-star'}
                            style={{
                              color: isActive ? '#FFD700' : '#ddd',
                              cursor: reviewLoading ? 'default' : 'pointer',
                              transition: 'color 0.2s',
                              opacity: reviewLoading ? 0.6 : 1
                            }}
                            onClick={() => !reviewLoading && setReviewRating(star)}
                            onMouseEnter={() => !reviewLoading && setHoveredStar(star)}
                            onMouseLeave={() => setHoveredStar(0)}
                          ></i>
                        );
                      })}
                    </div>
                    <div style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
                      {(hoveredStar || reviewRating) === 0 && 'اختر تقييمك'}
                      {(hoveredStar || reviewRating) === 1 && 'سيء جداً'}
                      {(hoveredStar || reviewRating) === 2 && 'سيء'}
                      {(hoveredStar || reviewRating) === 3 && 'جيد'}
                      {(hoveredStar || reviewRating) === 4 && 'جيد جداً'}
                      {(hoveredStar || reviewRating) === 5 && 'ممتاز'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>التعليق *</label>
                    <textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="شاركنا تجربتك..."
                      rows="5"
                      required
                      disabled={reviewLoading}
                      className="form-control"
                      dir="rtl"
                      maxLength="2000"
                    />
                    <small style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>احكي لنا عن تجربتك في هذا العقار</span>
                      <span>{reviewComment.length}/2000</span>
                    </small>
                  </div>

                  {reviewError && (
                    <div className="alert alert-danger" dir="rtl">
                      <i className="bi bi-exclamation-circle"></i> {reviewError}
                    </div>
                  )}

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={() => { setShowReviewModal(false); resetReviewModal(); }}
                      className="btn-secondary"
                      disabled={reviewLoading}
                    >
                      إلغاء
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={reviewLoading}
                      style={{ backgroundColor: '#FFD700', borderColor: '#FFD700', color: '#000' }}
                    >
                      {reviewLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          جاري الإرسال...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send"></i>
                          إرسال التقييم
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              /* Success Message */
              <div className="cancel-success">
                <i className="bi bi-check-circle-fill text-success"></i>
                <h3>شكراً لتقييمك!</h3>
                <p>{reviewResult.message}</p>
                <small style={{ display: 'block', marginTop: '12px', color: '#666' }}>
                  سيظهر تقييمك على الصفحة بعد مراجعته من قبل فريقنا
                </small>

                <button
                  onClick={() => { setShowReviewModal(false); resetReviewModal(); }}
                  className="btn-primary"
                >
                  إغلاق
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div
          className={`modal-overlay ${cancelLoading ? 'loading' : ''}`}
          onClick={() => {
            // Prevent closing during API call
            if (!cancelLoading) {
              setShowCancelModal(false);
              resetCancelModal();
            }
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {!cancelResult ? (
              <>
                <h3>إلغاء الحجز</h3>

                {/* Warning */}
                <div className="cancel-warning">
                  <i className="bi bi-exclamation-triangle"></i>
                  <p>هل أنت متأكد من إلغاء هذا الحجز؟</p>
                </div>

                {/* Cancellation Policy Info */}
                <div className="cancel-policy-info">
                  <h4>سياسة الإلغاء</h4>
                  <ul>
                    <li>
                      الإلغاء قبل {booking?.property?.cancellationPolicy?.fullRefundDays || 14} يوم: استرداد كامل
                    </li>
                    <li>
                      الإلغاء قبل {booking?.property?.cancellationPolicy?.partialRefundDays || 7} أيام:
                      استرداد جزئي ({booking?.property?.cancellationPolicy?.partialRefundPercent || 50}%)
                    </li>
                    <li>
                      الإلغاء أقل من {booking?.property?.cancellationPolicy?.partialRefundDays || 7} أيام: لا يوجد استرداد
                    </li>
                  </ul>
                </div>

                {/* Cancellation Form */}
                <form onSubmit={handleCancelBooking} className="cancel-form">
                  <div className="form-group">
                    <label>البريد الإلكتروني للتأكيد *</label>
                    <input
                      type="email"
                      value={cancelEmail}
                      onChange={(e) => setCancelEmail(e.target.value)}
                      placeholder="example@email.com"
                      required
                      disabled={cancelLoading}
                      dir="ltr"
                      className="form-control"
                    />
                    <small>يرجى إدخال البريد الإلكتروني المستخدم في الحجز للتحقق من هويتك</small>
                  </div>

                  <div className="form-group">
                    <label>سبب الإلغاء (اختياري)</label>
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="يرجى إخبارنا بسبب الإلغاء"
                      rows="3"
                      disabled={cancelLoading}
                      className="form-control"
                      dir="rtl"
                    />
                  </div>

                  {cancelError && (
                    <div className="alert alert-danger" dir="rtl">
                      <i className="bi bi-exclamation-circle"></i> {cancelError}
                    </div>
                  )}

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={() => { setShowCancelModal(false); resetCancelModal(); }}
                      className="btn-secondary"
                      disabled={cancelLoading}
                    >
                      إلغاء
                    </button>
                    <button
                      type="submit"
                      className="btn-danger"
                      disabled={cancelLoading}
                    >
                      {cancelLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          جاري الإلغاء...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-x-circle"></i>
                          تأكيد الإلغاء
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              /* Success Message */
              <div className="cancel-success">
                <i className="bi bi-check-circle-fill text-success"></i>
                <h3>تم إلغاء الحجز بنجاح</h3>
                <p>{cancelResult.message}</p>

                {cancelResult.refund?.eligible && (
                  <div className="refund-info">
                    <h4>معلومات الاسترداد</h4>
                    <p>المبلغ: {cancelResult.refund.amount} ريال ({cancelResult.refund.percentage}%)</p>
                    <small>{cancelResult.refund.reason}</small>
                  </div>
                )}

                <button
                  onClick={() => { setShowCancelModal(false); resetCancelModal(); }}
                  className="btn-primary"
                >
                  إغلاق
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

export default BookingLookup;
